/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/TestCasesTab.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                         |
 * |---|------------------------|-------------------|------------------|--------------------------------------|
 * | 1 | 2026-06-18 (Phase 3b)  | Claude Sonnet 4.6 | 4-tab nav        | Created — full-screen test library   |
 * | 2 | 2026-06-18 (Phase 3d)  | Claude Sonnet 4.6 | Copy/Move/Drag   | Sub-folder title fix; ⋮ context menu; Duplicate; MoveToModal; drag-drop via @dnd-kit; bulk Move Selected |
 * | 3 | 2026-06-18 (rename)    | Claude Sonnet 4.6 | Rename           | Rename option for suites, sub-folders (✏ button in hover actions) and test cases (✏ Rename in ⋮ menu → modal) |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<TestCasesTab />)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Sub-folder dialog: when showNewSuiteDialog === "root" → "New Suite"; when
 *    showNewSuiteDialog is a suite ULID → "New Sub-folder" with parent hint.
 * 2. ⋮ context menu: closes on outside-click via useEffect + document listener.
 * 3. Drag-drop: test rows are useDraggable; suite tree items are useDroppable.
 *    DragOverlay renders a floating ghost. moveTestCase() called on drop.
 * 4. Dexie update() calls use `as any` cast to avoid TS2615 (recursive IR types
 *    cause UpdateSpec<TestCase> key-path inference to fail — same pattern as db/index.ts).
 *
 * =============================================================================
 */

import { useState, useRef, useEffect, useCallback } from "react";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useLiveQuery } from "dexie-react-hooks";
import {
  db, createSuite, renameSuite, deleteSuite, deleteTestCase,
  moveTestCase, moveTestCases, duplicateTestCase, saveTestCase,
} from "../db/index.js";
import type { Suite, TestCase } from "../db/schema.js";
import { validateIR } from "@mimiq/ir";
import { downloadTestCase, downloadTestCasesAsZip, extractJsonsFromZip } from "../utils/exportUtils.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TestCasesTabProps {
  activeTestCaseId: string | null;
  onLoadTestCase: (tc: TestCase) => void;
  onRunSelected: (tests: TestCase[]) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusIcon(s: TestCase["lastRunStatus"]): string {
  if (s === "passed") return "✓";
  if (s === "passed_healed") return "⚕";
  if (s === "failed") return "✗";
  if (s === "error") return "!";
  return "—";
}

function statusClass(s: TestCase["lastRunStatus"]): string {
  return `tc-status--${s ?? "none"}`;
}

function formatDate(ts: number | undefined): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function collectSuiteIds(suiteId: string, allSuites: Suite[]): string[] {
  const ids: string[] = [suiteId];
  function walk(parentId: string) {
    for (const s of allSuites) {
      if (s.parentId === parentId) { ids.push(s.id); walk(s.id); }
    }
  }
  walk(suiteId);
  return ids;
}

/** Returns suites as a flat list ordered for the move-to picker (parent before children). */
function flattenSuiteTree(suites: Suite[], parentId?: string, depth = 0): { suite: Suite; depth: number }[] {
  const result: { suite: Suite; depth: number }[] = [];
  for (const s of suites.filter((s) => s.parentId === parentId)) {
    result.push({ suite: s, depth });
    result.push(...flattenSuiteTree(suites, s.id, depth + 1));
  }
  return result;
}

// ── Root component ────────────────────────────────────────────────────────────

export function TestCasesTab({ activeTestCaseId, onLoadTestCase, onRunSelected }: TestCasesTabProps) {
  const suites    = useLiveQuery(() => db.suites.orderBy("createdAt").toArray(), []) ?? [];
  const testCases = useLiveQuery(() => db.testCases.orderBy("createdAt").toArray(), []) ?? [];

  const [selectedSuiteId, setSelectedSuiteId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  // ── Export / import state ──────────────────────────────────────────────────
  const [filterText, setFilterText]     = useState("");
  const [filterStatus, setFilterStatus] = useState<"all"|"passed"|"failed"|"passed_healed"|"error">("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo]     = useState("");
  const [importToast, setImportToast]       = useState<string | null>(null);
  const [exporting, setExporting]           = useState(false);
  const [expandedSuites, setExpandedSuites] = useState<Set<string>>(new Set());
  const [renamingSuiteId, setRenamingSuiteId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [showNewSuiteDialog, setShowNewSuiteDialog] = useState<string | null>(null);
  const [newSuiteName, setNewSuiteName] = useState("");

  // ⋮ context menu
  const [contextMenuTcId, setContextMenuTcId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);

  // Rename test case modal
  const [renameTcId, setRenameTcId] = useState<string | null>(null);
  const [renameTcValue, setRenameTcValue] = useState("");

  // MoveToModal
  const [moveModalTcIds, setMoveModalTcIds] = useState<string[]>([]); // single or multi
  const [moveTargetSuiteId, setMoveTargetSuiteId] = useState<string>("");

  // Drag-drop active item
  const [activeDragTcId, setActiveDragTcId] = useState<string | null>(null);

  // ── Tag management ─────────────────────────────────────────────────────────
  const [editingTagsTcId, setEditingTagsTcId] = useState<string | null>(null);
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [showBulkTagAdd, setShowBulkTagAdd] = useState(false);
  const [showBulkTagRemove, setShowBulkTagRemove] = useState(false);

  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Filter tests by selected suite
  const visibleTests = (() => {
    let list = selectedSuiteId
      ? testCases.filter((tc) => collectSuiteIds(selectedSuiteId, suites).includes(tc.suiteId))
      : testCases;
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      list = list.filter((tc) => tc.name.toLowerCase().includes(q) || tc.tags.some((t) => t.toLowerCase().includes(q)));
    }
    if (filterStatus !== "all") list = list.filter((tc) => tc.lastRunStatus === filterStatus);
    if (filterDateFrom) {
      const from = new Date(filterDateFrom).getTime();
      list = list.filter((tc) => tc.createdAt >= from);
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo).getTime() + 86_400_000;
      list = list.filter((tc) => tc.createdAt <= to);
    }
    if (filterTag) list = list.filter((tc) => tc.tags.includes(filterTag));
    return list;
  })();

  const allChecked = visibleTests.length > 0 && visibleTests.every((tc) => checkedIds.has(tc.id));
  const checkedTests = visibleTests.filter((tc) => checkedIds.has(tc.id));

  // All unique tags across all test cases (for filter chips + autocomplete)
  const allTags = [...new Set(testCases.flatMap((tc) => tc.tags))].sort();

  async function saveTagsForTc(tcId: string, newTags: string[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (db.testCases.update as any)(tcId, { tags: newTags, updatedAt: Date.now() });
  }

  async function handleBulkAddTag(tag: string) {
    const clean = tag.trim().toLowerCase();
    if (!clean) return;
    for (const tc of checkedTests) {
      if (!tc.tags.includes(clean)) {
        await saveTagsForTc(tc.id, [...tc.tags, clean]);
      }
    }
    setBulkTagInput(""); setShowBulkTagAdd(false);
  }

  async function handleBulkRemoveTag(tag: string) {
    const clean = tag.trim().toLowerCase();
    if (!clean) return;
    for (const tc of checkedTests) {
      await saveTagsForTc(tc.id, tc.tags.filter((t) => t !== clean));
    }
    setBulkTagInput(""); setShowBulkTagRemove(false);
  }

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenuTcId) return;
    function handle(e: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenuTcId(null);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [contextMenuTcId]);

  function toggleCheck(id: string) {
    setCheckedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  function toggleAll() {
    if (allChecked) {
      setCheckedIds((prev) => { const next = new Set(prev); visibleTests.forEach((tc) => next.delete(tc.id)); return next; });
    } else {
      setCheckedIds((prev) => new Set([...prev, ...visibleTests.map((tc) => tc.id)]));
    }
  }

  // ── Export handlers ────────────────────────────────────────────────────────

  async function handleExportSelected() {
    if (checkedTests.length === 0) return;
    setExporting(true);
    try {
      await downloadTestCasesAsZip(checkedTests);
    } finally { setExporting(false); }
  }

  async function handleExportAll() {
    if (visibleTests.length === 0) return;
    setExporting(true);
    try {
      await downloadTestCasesAsZip(visibleTests);
    } finally { setExporting(false); }
  }

  async function handleImportFromZip() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".zip,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (file.name.endsWith(".zip")) {
        let entries;
        try { entries = await extractJsonsFromZip(file); }
        catch { alert(`Could not read ZIP: ${file.name}`); return; }

        let saved = 0;
        const errors: string[] = [];
        for (const entry of entries) {
          try {
            const parsed = JSON.parse(entry.jsonText) as unknown;
            const result = validateIR(parsed);
            if (!result.ok) { errors.push(`${entry.filename}: invalid format`); continue; }
            const ir = result.ir;
            const defaultSuiteId = suites[0]?.id ?? "root";
            await saveTestCase({
              name: ir.meta.name ?? entry.filename.replace(/\.json$/, ""),
              suiteId: defaultSuiteId,
              tags: ir.meta.tags ?? [],
              ir,
            });
            saved++;
          } catch { errors.push(entry.filename); }
        }
        const msg = errors.length > 0
          ? `${saved} imported · ${errors.length} failed: ${errors.join(", ")}`
          : `${saved} test case${saved !== 1 ? "s" : ""} imported to library`;
        setImportToast(msg);
        setTimeout(() => setImportToast(null), 4000);
      } else {
        // Single JSON — validate + save to library
        try {
          const text = await file.text();
          const parsed = JSON.parse(text) as unknown;
          const result = validateIR(parsed);
          if (!result.ok) { alert(`Invalid Mimiq test file: ${file.name}`); return; }
          const ir = result.ir;
          const defaultSuiteId = suites[0]?.id ?? "root";
          await saveTestCase({ name: ir.meta.name ?? file.name.replace(/\.json$/, ""), suiteId: defaultSuiteId, tags: ir.meta.tags ?? [], ir });
          setImportToast(`"${ir.meta.name}" imported to library`);
          setTimeout(() => setImportToast(null), 3000);
        } catch { alert(`Could not read file: ${file.name}`); }
      }
    };
    input.click();
  }

  function toggleSuite(id: string) {
    setExpandedSuites((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  function openNewSuiteDialog(parentId: string | "root") {
    setNewSuiteName("");
    setShowNewSuiteDialog(parentId);
  }

  async function handleCreateSuite() {
    if (!newSuiteName.trim()) return;
    const parentId = (!showNewSuiteDialog || showNewSuiteDialog === "root") ? undefined : showNewSuiteDialog;
    const s = await createSuite(newSuiteName.trim(), parentId);
    setExpandedSuites((prev) => new Set([...prev, s.id]));
    setShowNewSuiteDialog(null);
    setNewSuiteName("");
  }

  async function startRenameSuite(s: Suite) {
    setRenamingSuiteId(s.id);
    setRenameValue(s.name);
    setTimeout(() => renameInputRef.current?.select(), 0);
  }

  async function commitRenameSuite() {
    if (renamingSuiteId && renameValue.trim()) await renameSuite(renamingSuiteId, renameValue.trim());
    setRenamingSuiteId(null);
  }

  async function handleDeleteSuite(s: Suite) {
    const count = testCases.filter((tc) => collectSuiteIds(s.id, suites).includes(tc.suiteId)).length;
    if (count > 0 && !confirm(`Delete suite "${s.name}" and its ${count} test(s)?`)) return;
    if (selectedSuiteId && collectSuiteIds(s.id, suites).includes(selectedSuiteId)) setSelectedSuiteId(null);
    await deleteSuite(s.id);
  }

  async function handleDeleteTest(tc: TestCase) {
    if (!confirm(`Delete "${tc.name}"?`)) return;
    setCheckedIds((prev) => { const next = new Set(prev); next.delete(tc.id); return next; });
    await deleteTestCase(tc.id);
  }

  async function handleDuplicate(tc: TestCase) {
    setContextMenuTcId(null);
    await duplicateTestCase(tc.id);
  }

  function openRenameModal(tc: TestCase) {
    setContextMenuTcId(null);
    setRenameTcId(tc.id);
    setRenameTcValue(tc.name);
  }

  async function handleConfirmRenameTestCase() {
    if (!renameTcId || !renameTcValue.trim()) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (db.testCases.update as any)(renameTcId, { name: renameTcValue.trim(), updatedAt: Date.now() });
    setRenameTcId(null);
  }

  function openMoveModal(tcIds: string[]) {
    setContextMenuTcId(null);
    const firstTc = testCases.find((tc) => tc.id === tcIds[0]);
    setMoveTargetSuiteId(firstTc?.suiteId ?? (suites[0]?.id ?? ""));
    setMoveModalTcIds(tcIds);
  }

  async function handleConfirmMove() {
    if (!moveTargetSuiteId || moveModalTcIds.length === 0) return;
    if (moveModalTcIds.length === 1) {
      await moveTestCase(moveModalTcIds[0]!, moveTargetSuiteId);
    } else {
      await moveTestCases(moveModalTcIds, moveTargetSuiteId);
    }
    setCheckedIds((prev) => { const next = new Set(prev); moveModalTcIds.forEach((id) => next.delete(id)); return next; });
    setMoveModalTcIds([]);
  }

  // dnd-kit handlers
  function handleDragStart(event: DragStartEvent) {
    setActiveDragTcId(String(event.active.id));
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveDragTcId(null);
    const { active, over } = event;
    if (!over) return;
    const tcId = String(active.id);
    const suiteId = String(over.id);
    if (suites.find((s) => s.id === suiteId)) {
      await moveTestCase(tcId, suiteId);
    }
  }

  const activeDragTc = activeDragTcId ? testCases.find((tc) => tc.id === activeDragTcId) : null;
  const flatSuites = flattenSuiteTree(suites, undefined, 0);

  // ── Suite tree renderer ────────────────────────────────────────────────────

  function renderSuiteTree(parentId: string | undefined, depth: number): React.ReactNode {
    return suites
      .filter((s) => s.parentId === parentId)
      .map((s) => {
        const isExpanded = expandedSuites.has(s.id);
        const hasChildren = suites.some((c) => c.parentId === s.id) || testCases.some((tc) => tc.suiteId === s.id);
        const isSelected = selectedSuiteId === s.id;
        const isRenaming = renamingSuiteId === s.id;
        const count = testCases.filter((tc) => collectSuiteIds(s.id, suites).includes(tc.suiteId)).length;

        return (
          <DroppableSuite key={s.id} suiteId={s.id} depth={depth}>
            <div className={`tcl-suite-row${isSelected ? " tcl-suite-row--selected" : ""}`}>
              <button className="tcl-suite-toggle" onClick={() => toggleSuite(s.id)}>
                {hasChildren ? (isExpanded ? "▾" : "▸") : " "}
              </button>
              {isRenaming ? (
                <input
                  ref={renameInputRef}
                  className="tcl-rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") void commitRenameSuite(); if (e.key === "Escape") setRenamingSuiteId(null); }}
                  onBlur={() => void commitRenameSuite()}
                  autoFocus
                />
              ) : (
                <button
                  className="tcl-suite-name"
                  onClick={() => setSelectedSuiteId(isSelected ? null : s.id)}
                  onDoubleClick={() => void startRenameSuite(s)}
                  title="Click to filter · Double-click to rename"
                >
                  📁 {s.name} <span className="tcl-suite-count">({count})</span>
                </button>
              )}
              <div className="tcl-suite-actions">
                <button className="tcl-action-btn" title="Rename" onClick={() => void startRenameSuite(s)}>✏</button>
                <button className="tcl-action-btn" title="New sub-folder" onClick={() => {
                  setExpandedSuites((prev) => new Set([...prev, s.id]));
                  openNewSuiteDialog(s.id);
                }}>+</button>
                <button className="tcl-action-btn tcl-action-btn--danger" title="Delete" onClick={() => void handleDeleteSuite(s)}>✕</button>
              </div>
            </div>
            {isExpanded && renderSuiteTree(s.id, depth + 1)}
          </DroppableSuite>
        );
      });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={(e) => void handleDragEnd(e)}>
    <div className="test-cases-tab">
      {/* ── Header ── */}
      <div className="tcl-header">
        <h2 className="tcl-title">Test Cases</h2>
        <div className="tcl-header-actions">
          {checkedTests.length > 0 && (
            <>
              <button className="btn btn--primary" onClick={() => { onRunSelected(checkedTests); setCheckedIds(new Set()); }}>
                ▶ Run Selected ({checkedTests.length})
              </button>
              <button className="btn" onClick={() => openMoveModal(checkedTests.map((tc) => tc.id))}>
                ↗ Move Selected ({checkedTests.length})
              </button>
              {/* Bulk tag actions */}
              <div style={{ position: "relative" }}>
                <button className="btn" title="Add a tag to all selected tests"
                  onClick={() => { setShowBulkTagAdd((v) => !v); setShowBulkTagRemove(false); setBulkTagInput(""); }}>
                  + Tag
                </button>
                {showBulkTagAdd && (
                  <div className="bulk-tag-popover">
                    <span className="bulk-tag-label">Add tag to {checkedTests.length} test{checkedTests.length !== 1 ? "s" : ""}:</span>
                    <input className="bulk-tag-input" placeholder="tag name…" value={bulkTagInput} autoFocus
                      onChange={(e) => setBulkTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") void handleBulkAddTag(bulkTagInput); if (e.key === "Escape") setShowBulkTagAdd(false); }} />
                    {allTags.filter((t) => t.includes(bulkTagInput.toLowerCase()) && !checkedTests.every((tc) => tc.tags.includes(t))).slice(0, 6).map((t) => (
                      <button key={t} className="bulk-tag-suggestion" onMouseDown={() => void handleBulkAddTag(t)}>{t}</button>
                    ))}
                    <button className="btn btn--primary" style={{ fontSize: "0.72rem", padding: "0.2rem 0.6rem" }} onClick={() => void handleBulkAddTag(bulkTagInput)} disabled={!bulkTagInput.trim()}>Add</button>
                  </div>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <button className="btn" title="Remove a tag from all selected tests"
                  onClick={() => { setShowBulkTagRemove((v) => !v); setShowBulkTagAdd(false); setBulkTagInput(""); }}>
                  - Tag
                </button>
                {showBulkTagRemove && (
                  <div className="bulk-tag-popover">
                    <span className="bulk-tag-label">Remove tag from {checkedTests.length} test{checkedTests.length !== 1 ? "s" : ""}:</span>
                    {[...new Set(checkedTests.flatMap((tc) => tc.tags))].map((t) => (
                      <button key={t} className="bulk-tag-suggestion bulk-tag-suggestion--remove"
                        onMouseDown={() => void handleBulkRemoveTag(t)}>✕ {t}</button>
                    ))}
                    {checkedTests.every((tc) => tc.tags.length === 0) && <span className="bulk-tag-label">No tags on selected tests</span>}
                  </div>
                )}
              </div>
              <button className="btn btn--export-tc" onClick={handleExportSelected} disabled={exporting}>
                ↓ Export Selected ({checkedTests.length})
              </button>
            </>
          )}
          {/* Run filtered tests by tag */}
          {filterTag && visibleTests.length > 0 && checkedTests.length === 0 && (
            <button className="btn btn--primary"
              onClick={() => { onRunSelected(visibleTests); setFilterTag(null); }}
              title={`Run all ${visibleTests.length} tests tagged "${filterTag}"`}>
              ▶ Run "{filterTag}" ({visibleTests.length})
            </button>
          )}
          <button className="btn btn--export-tc" onClick={handleExportAll} disabled={exporting || visibleTests.length === 0}
            title={`Export all ${visibleTests.length} visible test cases`}>
            ↓ Export All
          </button>
          <button className="btn" onClick={handleImportFromZip} title="Import test cases from .json or .zip file">
            ↑ Import
          </button>
          <button className="btn" onClick={() => openNewSuiteDialog("root")}>+ New Suite</button>
        </div>
      </div>

      {/* ── New Suite / New Sub-folder modal ── */}
      {showNewSuiteDialog !== null && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowNewSuiteDialog(null); setNewSuiteName(""); } }}>
          <div className="modal-box">
            <div className="modal-header">
              <strong>{showNewSuiteDialog === "root" ? "New Suite" : "New Sub-folder"}</strong>
              <button className="modal-close" onClick={() => { setShowNewSuiteDialog(null); setNewSuiteName(""); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label className="modal-label">
                  {showNewSuiteDialog === "root" ? "Suite name" : "Sub-folder name"}
                </label>
                <input
                  className="modal-input"
                  placeholder={showNewSuiteDialog === "root" ? "e.g. Login flows" : "e.g. Edge cases"}
                  value={newSuiteName}
                  onChange={(e) => setNewSuiteName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void handleCreateSuite(); }
                    if (e.key === "Escape") { setShowNewSuiteDialog(null); setNewSuiteName(""); }
                  }}
                  autoFocus
                />
              </div>
              {showNewSuiteDialog !== "root" && (
                <p className="modal-hint">
                  Inside: <strong>{suites.find((s) => s.id === showNewSuiteDialog)?.name ?? ""}</strong>
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn--primary" disabled={!newSuiteName.trim()} onClick={() => void handleCreateSuite()}>
                {showNewSuiteDialog === "root" ? "Create Suite" : "Create Sub-folder"}
              </button>
              <button className="btn" onClick={() => { setShowNewSuiteDialog(null); setNewSuiteName(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Move to suite modal ── */}
      {moveModalTcIds.length > 0 && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setMoveModalTcIds([]); }}>
          <div className="modal-box">
            <div className="modal-header">
              <strong>Move to suite</strong>
              <button className="modal-close" onClick={() => setMoveModalTcIds([])}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label className="modal-label">
                  {moveModalTcIds.length === 1
                    ? `Moving: "${testCases.find((tc) => tc.id === moveModalTcIds[0])?.name ?? ""}"`
                    : `Moving ${moveModalTcIds.length} tests`}
                </label>
              </div>
              <div className="tcl-move-suite-list">
                {flatSuites.map(({ suite, depth }) => (
                  <label key={suite.id} className={`tcl-move-suite-item${moveTargetSuiteId === suite.id ? " tcl-move-suite-item--selected" : ""}`}
                    style={{ paddingLeft: 12 + depth * 16 }}>
                    <input
                      type="radio"
                      name="moveTarget"
                      value={suite.id}
                      checked={moveTargetSuiteId === suite.id}
                      onChange={() => setMoveTargetSuiteId(suite.id)}
                    />
                    <span>📁 {suite.name}</span>
                    <span className="tcl-suite-count">
                      ({testCases.filter((tc) => tc.suiteId === suite.id).length})
                    </span>
                  </label>
                ))}
                {suites.length === 0 && (
                  <p className="modal-hint">No suites exist. Create one first.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn--primary"
                disabled={!moveTargetSuiteId}
                onClick={() => void handleConfirmMove().then(() => setMoveModalTcIds([]))}
              >
                Move Here
              </button>
              <button className="btn" onClick={() => setMoveModalTcIds([])}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rename test case modal ── */}
      {renameTcId !== null && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setRenameTcId(null); }}>
          <div className="modal-box">
            <div className="modal-header">
              <strong>Rename Test Case</strong>
              <button className="modal-close" onClick={() => setRenameTcId(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label className="modal-label">New name</label>
                <input
                  className="modal-input"
                  value={renameTcValue}
                  onChange={(e) => setRenameTcValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void handleConfirmRenameTestCase(); }
                    if (e.key === "Escape") setRenameTcId(null);
                  }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn--primary"
                disabled={!renameTcValue.trim()}
                onClick={() => void handleConfirmRenameTestCase()}
              >
                Rename
              </button>
              <button className="btn" onClick={() => setRenameTcId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="tcl-filter-bar">
        <input
          className="tcl-filter-input"
          placeholder="Search by name or tag…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        <select className="tcl-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}>
          <option value="all">All statuses</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
          <option value="passed_healed">Healed</option>
          <option value="error">Error</option>
        </select>
        <label className="tcl-filter-date-label">Created after</label>
        <input type="date" className="tcl-filter-date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
        <label className="tcl-filter-date-label">before</label>
        <input type="date" className="tcl-filter-date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
        {(filterText || filterStatus !== "all" || filterDateFrom || filterDateTo || filterTag) && (
          <button className="tcl-filter-clear" onClick={() => { setFilterText(""); setFilterStatus("all"); setFilterDateFrom(""); setFilterDateTo(""); setFilterTag(null); }}>
            ✕ Clear
          </button>
        )}
        <span className="tcl-filter-count">{visibleTests.length} test{visibleTests.length !== 1 ? "s" : ""}</span>
      </div>

      {/* ── Tag filter chips ── */}
      {allTags.length > 0 && (
        <div className="tcl-tag-filter-bar">
          <span className="tcl-tag-filter-label">Filter by tag:</span>
          {allTags.map((tag) => (
            <button
              key={tag}
              className={`tcl-tag-filter-chip${filterTag === tag ? " tcl-tag-filter-chip--active" : ""}`}
              onClick={() => setFilterTag(filterTag === tag ? null : tag)}
              title={`${testCases.filter((tc) => tc.tags.includes(tag)).length} tests`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* ── Import toast ── */}
      {importToast && (
        <div className="tc-import-toast">{importToast}</div>
      )}

      <div className="tcl-body">
        {/* ── Left: Suite tree (droppable targets) ── */}
        <div className="tcl-suite-panel">
          <div className="tcl-suite-tree-header">
            <button
              className={`tcl-all-btn${!selectedSuiteId ? " tcl-all-btn--active" : ""}`}
              onClick={() => setSelectedSuiteId(null)}
            >
              All Tests ({testCases.length})
            </button>
          </div>
          <div className="tcl-suite-tree">
            {renderSuiteTree(undefined, 0)}
          </div>
        </div>

        {/* ── Right: Test grid (draggable rows) ── */}
        <div className="tcl-grid-panel">
          {visibleTests.length === 0 ? (
            <div className="tcl-empty">
              {selectedSuiteId
                ? "No tests in this suite. Record a test and save it here, or drag one from another suite."
                : "No tests yet. Click + New Suite, then use the Recorder to record and save your first test."}
            </div>
          ) : (
            <table className="tcl-table">
              <thead>
                <tr>
                  <th className="tcl-th-check">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} title="Select all visible tests" />
                  </th>
                  <th></th>{/* drag handle column */}
                  <th>Name</th>
                  <th>Suite</th>
                  <th>Last Run</th>
                  <th>Status</th>
                  <th>Tags</th>
                  <th></th>{/* actions column */}
                </tr>
              </thead>
              <tbody>
                {visibleTests.map((tc) => {
                  const suite = suites.find((s) => s.id === tc.suiteId);
                  const isActive = activeTestCaseId === tc.id;
                  return (
                    <DraggableTestRow
                      key={tc.id}
                      tc={tc}
                      suite={suite}
                      isActive={isActive}
                      isDragging={activeDragTcId === tc.id}
                      isChecked={checkedIds.has(tc.id)}
                      onCheck={() => toggleCheck(tc.id)}
                      onSelect={() => onLoadTestCase(tc)}
                      contextMenuTcId={contextMenuTcId}
                      contextMenuRef={contextMenuRef}
                      onOpenMenu={() => setContextMenuTcId(tc.id)}
                      onCloseMenu={() => setContextMenuTcId(null)}
                      onDuplicate={() => void handleDuplicate(tc)}
                      onRename={() => openRenameModal(tc)}
                      onMoveToModal={() => openMoveModal([tc.id])}
                      onDelete={() => void handleDeleteTest(tc)}
                      onEditTags={() => setEditingTagsTcId((prev) => prev === tc.id ? null : tc.id)}
                      editingTags={editingTagsTcId === tc.id}
                      allTags={allTags}
                      onSaveTags={(newTags) => { void saveTagsForTc(tc.id, newTags); setEditingTagsTcId(null); }}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>

    {/* Drag overlay — floating ghost while dragging */}
    <DragOverlay>
      {activeDragTc && (
        <div className="tcl-drag-ghost">
          📄 {activeDragTc.name}
        </div>
      )}
    </DragOverlay>
    </DndContext>
  );
}

// ── DroppableSuite ────────────────────────────────────────────────────────────

function DroppableSuite({ suiteId, depth, children }: { suiteId: string; depth: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: suiteId });
  return (
    <div ref={setNodeRef} style={{ marginLeft: depth * 14 }} className={isOver ? "tcl-suite-drop-over" : ""}>
      {children}
    </div>
  );
}

// ── DraggableTestRow ──────────────────────────────────────────────────────────

interface DraggableTestRowProps {
  tc: TestCase;
  suite: Suite | undefined;
  isActive: boolean;
  isDragging: boolean;
  isChecked: boolean;
  onCheck: () => void;
  onSelect: () => void;
  contextMenuTcId: string | null;
  contextMenuRef: React.RefObject<HTMLDivElement | null>;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onDuplicate: () => void;
  onRename: () => void;
  onMoveToModal: () => void;
  onDelete: () => void;
  onEditTags: () => void;
  editingTags: boolean;
  allTags: string[];
  onSaveTags: (newTags: string[]) => void;
}

function DraggableTestRow({
  tc, suite, isActive, isDragging, isChecked,
  onCheck, onSelect,
  contextMenuTcId, contextMenuRef, onOpenMenu, onCloseMenu,
  onDuplicate, onRename, onMoveToModal, onDelete,
  onEditTags, editingTags, allTags, onSaveTags,
}: DraggableTestRowProps) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: tc.id });
  const [localTags, setLocalTags] = useState<string[]>(tc.tags);
  const [tagInput, setTagInput] = useState("");
  // Keep local tags in sync when tc changes from outside
  if (!editingTags && localTags !== tc.tags) setLocalTags(tc.tags);

  function commitTags() {
    onSaveTags(localTags);
    setTagInput("");
  }

  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: 0.4 }
    : {};

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`tcl-row${isActive ? " tcl-row--active" : ""}${isDragging ? " tcl-row--dragging" : ""}`}
      onClick={onSelect}
      title="Click to load into recorder · Drag to move to a suite"
    >
      <td className="tcl-td-check" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={isChecked} onChange={onCheck} />
      </td>
      {/* Drag handle */}
      <td className="tcl-td-handle" onClick={(e) => e.stopPropagation()}>
        <span className="tcl-drag-handle" {...attributes} {...listeners} title="Drag to move to a suite">⠿</span>
      </td>
      <td className="tcl-td-name">
        <span className="tcl-test-icon">📄</span>
        {tc.name}
      </td>
      <td className="tcl-td-suite">{suite?.name ?? "—"}</td>
      <td className="tcl-td-date">{formatDate(tc.updatedAt)}</td>
      <td className="tcl-td-status">
        <span className={`tcl-status ${statusClass(tc.lastRunStatus)}`}>
          {statusIcon(tc.lastRunStatus)}
          {tc.lastRunStatus === "passed" ? " Passed"
           : tc.lastRunStatus === "failed" ? " Failed"
           : tc.lastRunStatus === "passed_healed" ? " Healed"
           : tc.lastRunStatus === "error" ? " Error"
           : " Never run"}
        </span>
      </td>
      <td className="tcl-td-tags" onClick={(e) => { e.stopPropagation(); onEditTags(); }}>
        {editingTags ? (
          <div className="tag-input-wrap tag-input-wrap--inline" onClick={(e) => e.stopPropagation()}>
            {localTags.map((t) => (
              <span key={t} className="tcl-tag tcl-tag--editable">
                {t}<button className="tcl-tag-remove" onMouseDown={(e) => { e.preventDefault(); setLocalTags(localTags.filter((x) => x !== t)); }}>×</button>
              </span>
            ))}
            <div style={{ position: "relative", minWidth: 80, flex: 1 }}>
              <input
                className="tag-inline-input"
                placeholder="add tag…"
                value={tagInput}
                autoFocus
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                    e.preventDefault();
                    const clean = tagInput.trim().toLowerCase().replace(/,/g, "");
                    if (clean && !localTags.includes(clean)) setLocalTags([...localTags, clean]);
                    setTagInput("");
                  }
                  if (e.key === "Backspace" && !tagInput && localTags.length) setLocalTags(localTags.slice(0, -1));
                  if (e.key === "Escape") { commitTags(); }
                  if (e.key === "Tab") { e.preventDefault(); commitTags(); }
                }}
                onBlur={commitTags}
              />
              {tagInput && (
                <ul className="tag-input-dropdown">
                  {allTags.filter((t) => !localTags.includes(t) && t.includes(tagInput.toLowerCase())).slice(0, 6).map((t) => (
                    <li key={t}>
                      <button className="tag-input-dropdown-item" onMouseDown={(e) => { e.preventDefault(); if (!localTags.includes(t)) setLocalTags([...localTags, t]); setTagInput(""); }}>
                        {t}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="tcl-tags-display" title="Click to edit tags">
            {tc.tags.slice(0, 4).map((t) => <span key={t} className="tcl-tag">{t}</span>)}
            {tc.tags.length === 0 && <span className="tcl-tags-add-hint">+ tags</span>}
          </div>
        )}
      </td>
      {/* Actions: ⋮ menu */}
      <td className="tcl-td-actions" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
        <button
          className={`tcl-action-btn tcl-menu-btn${contextMenuTcId === tc.id ? " tcl-menu-btn--open" : ""}`}
          onClick={(e) => { e.stopPropagation(); contextMenuTcId === tc.id ? onCloseMenu() : onOpenMenu(); }}
          title="More actions"
        >
          ⋮
        </button>
        {contextMenuTcId === tc.id && (
          <div ref={contextMenuRef} className="tcl-context-menu">
            <button className="tcl-cm-item" onClick={onRename}>
              ✏ Rename
            </button>
            <button className="tcl-cm-item" onClick={() => { onCloseMenu(); onEditTags(); }}>
              🏷 Edit Tags
            </button>
            <button className="tcl-cm-item" onClick={onDuplicate}>
              📋 Duplicate
            </button>
            <button className="tcl-cm-item" onClick={onMoveToModal}>
              ↗ Move to…
            </button>
            <div className="tcl-cm-divider" />
            <button className="tcl-cm-item tcl-cm-item--danger" onClick={onDelete}>
              ✕ Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
